import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  CheckSquare, CheckCircle2, XCircle, Clock, User, Building2,
  Briefcase, Calendar, IndianRupee, ShieldAlert, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const fmt = (n) =>
  typeof n === "number"
    ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 })
    : "—";

const fmtDT = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

// ── Diff formatting helpers (Rev. 2026-08-04) ────────────────
// Human-readable label for snake_case field names in the diff table.
const FIELD_LABELS = {
  punch_id: "Punch ID", employee_code: "Employee Code",
  name: "Name", father_husband_name: "Father / Husband",
  dob: "Date of Birth", doj: "Date of Joining",
  gender: "Gender", marital_status: "Marital Status",
  aadhaar: "Aadhaar", pan: "PAN", uan: "UAN", esi_code: "ESI Code",
  mobile: "Mobile", alt_mobile: "Alt Mobile", email: "Email",
  address: "Address", city: "City", state: "State", pincode: "Pincode",
  unit_id: "Unit", department_id: "Department",
  designation: "Designation", category: "Category",
  shift_hours: "Shift Hours", shift_start: "Shift Start",
  status: "Status", is_active: "Active",
  pf_applicable: "PF Applicable", esi_applicable: "ESI Applicable",
  overtime_applicable: "Overtime Applicable", bonus_applicable: "Bonus Applicable",
  ctc: "CTC (monthly)", gross_salary: "Gross Salary",
  bank_account_no: "Bank Account", bank_ifsc: "Bank IFSC", bank_name: "Bank Name",
  emergency_contact_name: "Emergency Contact", emergency_contact_mobile: "Emergency Mobile",
  credit_in: "Credit Method",
};

// Enum → title-case map for known code-y values
const ENUM_LABELS = {
  blue_collar: "Blue Collar", white_collar: "White Collar",
  worker: "Worker", staff: "Staff",
  Working: "Working", Left: "Left", Terminated: "Terminated",
  Absconding: "Absconding",
  male: "Male", female: "Female", other: "Other",
  single: "Single", married: "Married",
  Bank: "Bank", Cash: "Cash", Cheque: "Cheque",
};

const prettifyLabel = (k) =>
  FIELD_LABELS[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Format a single value for the diff table. `field` is the key so we
// know whether to resolve a UUID via unitMap / deptMap.
function formatDiffValue(field, v, unitMap, deptMap) {
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (field === "unit_id") return unitMap?.[v] || v;
  if (field === "department_id") return deptMap?.[v] || v;
  if (typeof v === "string" && ENUM_LABELS[v]) return ENUM_LABELS[v];
  if (typeof v === "number" && (field === "ctc" || field === "gross_salary"))
    return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
  return String(v);
}

const STATUS_STYLES = {
  PENDING:  { className: "bg-amber-100 text-amber-800 border-amber-200",  icon: Clock },
  APPLYING: { className: "bg-blue-100 text-blue-800 border-blue-200",     icon: RefreshCw },
  APPLIED:  { className: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  REJECTED: { className: "bg-red-100 text-red-800 border-red-200",     icon: XCircle },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.PENDING;
  const Icon = s.icon;
  return (
    <span
      data-testid={`status-pill-${status}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${s.className}`}
    >
      <Icon className="h-3 w-3" />
      {status}
    </span>
  );
}

function ChangeTypeChip({ change }) {
  const label = {
    NEW_EMPLOYEE: "New Employee",
    EDIT_EMPLOYEE: "Edit Employee",
    SALARY_REVISION: "Salary Revision",
    EMPLOYEE_EXIT: "Employee Exit",
  }[change] || change;
  return (
    <span className="text-[10px] uppercase tracking-wider text-slate-500">
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// LEFT PANE — list of requests for the active tab
// ─────────────────────────────────────────────────────────────

function RequestListPane({
  rows, activeId, onSelect, loading, emptyMsg,
  bulkMode, selectedIds, onToggleSelect, currentUser,
}) {
  return (
    <div
      className="border-r border-slate-200 bg-white overflow-y-auto"
      data-testid="approvals-list-pane"
    >
      {loading && (
        <div className="p-6 text-sm text-slate-400 text-center">Loading…</div>
      )}
      {!loading && rows.length === 0 && (
        <div className="p-8 text-sm text-slate-400 text-center">{emptyMsg}</div>
      )}
      {!loading && rows.map((r) => {
        const isSelf = r.maker_id === currentUser?.id;
        return (
          <div
            key={r.id}
            data-testid={`approval-row-${r.id}`}
            className={`w-full flex items-start gap-2 px-3 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${
              activeId === r.id ? "bg-red-50/50 border-l-4 border-l-red-600" : ""
            }`}
          >
            {bulkMode && (
              <input
                type="checkbox"
                className="mt-1.5 h-3.5 w-3.5 accent-red-600 disabled:opacity-30"
                checked={selectedIds.has(r.id)}
                disabled={isSelf}
                title={isSelf ? "You submitted this — another Super Admin must approve it" : ""}
                onChange={() => onToggleSelect(r.id)}
                data-testid={`bulk-select-${r.id}`}
              />
            )}
            <button
              onClick={() => onSelect(r.id)}
              className="flex-1 text-left min-w-0"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <ChangeTypeChip change={r.change_type} />
                  <div className="font-semibold text-slate-900 mt-0.5 truncate">
                    {r.employee_name_at_submit || "—"}
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono truncate">
                    {r.punch_id_at_submit} · {r.employee_code_at_submit}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">
                    by {r.maker_name || "—"} · {fmtDT(r.created_at)}
                  </div>
                </div>
                <StatusPill status={r.status} />
              </div>
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// RIGHT PANE — request detail with salary snapshot
// ─────────────────────────────────────────────────────────────

function DetailPane({ detail, currentUser, onApprove, onReject, loading, unitMap, deptMap }) {
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");

  if (loading) {
    return (
      <div className="p-8 text-sm text-slate-400" data-testid="approvals-detail-loading">
        Loading detail…
      </div>
    );
  }
  if (!detail || !detail.request) {
    return (
      <div
        className="h-full flex items-center justify-center text-slate-400 text-sm"
        data-testid="approvals-detail-empty"
      >
        Select a request from the list.
      </div>
    );
  }

  const { request: req, salary_snapshot: snap, employee: emp } = detail;
  const isSelf   = req.maker_id === currentUser?.id;
  const canAct   = currentUser?.role === "super_admin" && !isSelf && req.status === "PENDING";
  const breakup  = snap?.breakup || {};
  const earn     = breakup.earnings || {};
  const ded      = breakup.employee_deductions || {};
  const net      = breakup.net || {};
  const er       = breakup.employer_contributions || {};
  const ctcBlock = breakup.ctc || {};

  const submitApprove = async () => {
    try {
      await onApprove(req.id, reason);
      setApproveOpen(false); setReason("");
      toast.success("Request approved. Employee is now active.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const submitReject = async () => {
    if (reason.trim().length < 3) {
      toast.error("Rejection reason must be at least 3 characters");
      return;
    }
    try {
      await onReject(req.id, reason.trim());
      setRejectOpen(false); setReason("");
      toast.success("Request rejected. Maker has been notified.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="overflow-y-auto p-6 space-y-6 bg-slate-50/40" data-testid="approvals-detail-pane">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <ChangeTypeChip change={req.change_type} />
              <StatusPill status={req.status} />
            </div>
            <h2
              className="text-2xl font-bold text-slate-900"
              data-testid="detail-employee-name"
              style={{ fontFamily: "Chivo" }}
            >
              {req.employee_name_at_submit}
            </h2>
            <div className="text-xs text-slate-500 font-mono">
              {req.punch_id_at_submit} · {req.employee_code_at_submit}
            </div>
          </div>
          {canAct && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setReason(""); setRejectOpen(true); }}
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="btn-reject-request"
              >
                <XCircle className="h-4 w-4 mr-1" />
                Reject
              </Button>
              <Button
                onClick={() => { setReason(""); setApproveOpen(true); }}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-approve-request"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" />
                Approve
              </Button>
            </div>
          )}
          {isSelf && req.status === "PENDING" && (
            <Badge variant="outline" className="text-slate-500">
              You are the maker — another Super Admin must action this
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 text-sm">
          <InfoRow icon={User} label="Maker">
            {req.maker_name} <span className="text-slate-400">({req.maker_role})</span>
          </InfoRow>
          <InfoRow icon={Clock} label="Submitted">{fmtDT(req.created_at)}</InfoRow>
          {emp?.doj && <InfoRow icon={Calendar} label="Date of Joining">{emp.doj}</InfoRow>}
          {emp?.designation && (
            <InfoRow icon={Briefcase} label="Designation">{emp.designation}</InfoRow>
          )}
          {req.unit_id_at_submit && (
            <InfoRow icon={Building2} label="Unit">
              {(unitMap && unitMap[req.unit_id_at_submit]) || (
                <span className="font-mono text-[11px]">{req.unit_id_at_submit}</span>
              )}
            </InfoRow>
          )}
          {emp?.ctc != null && (
            <InfoRow icon={IndianRupee} label="CTC (monthly)">
              ₹{fmt(emp.ctc)}
            </InfoRow>
          )}
          {req.status === "APPLIED" && (
            <>
              <InfoRow icon={CheckCircle2} label="Approved by">
                {req.approver_name}
              </InfoRow>
              <InfoRow icon={Clock} label="Approved at">
                {fmtDT(req.applied_at)}
              </InfoRow>
            </>
          )}
          {req.status === "REJECTED" && (
            <>
              <InfoRow icon={XCircle} label="Rejected by">
                {req.approver_name}
              </InfoRow>
              <InfoRow icon={Clock} label="Rejected at">
                {fmtDT(req.decision_at)}
              </InfoRow>
              {req.decision_reason && (
                <div className="md:col-span-2 text-sm bg-red-50 border border-red-200 rounded p-3 mt-1">
                  <div className="text-[10px] uppercase tracking-wider text-red-600 font-semibold mb-1">
                    Reason
                  </div>
                  {req.decision_reason}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Field-level diff for EDIT_EMPLOYEE ─────────────── */}
      {req.change_type === "EDIT_EMPLOYEE" && (
        <div className="bg-white rounded-lg border-l-4 border-l-red-500 border border-slate-200 p-5">
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500"></span>
            Requested Changes ({Object.keys(req.payload_after || {}).length} field
            {Object.keys(req.payload_after || {}).length === 1 ? "" : "s"})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="edit-diff-table">
              <thead className="text-left border-b border-slate-200">
                <tr>
                  <th className="py-2 px-2 font-semibold text-[10px] uppercase tracking-wider text-slate-500 w-1/3">Field</th>
                  <th className="py-2 px-2 font-semibold text-[10px] uppercase tracking-wider text-slate-500 w-1/3">Current</th>
                  <th className="py-2 px-2 font-semibold text-[10px] uppercase tracking-wider text-red-600 w-1/3">Proposed (changes)</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(req.payload_after || {}).map(([k, v]) => {
                  const before = (req.payload_before || {})[k];
                  return (
                    <tr key={k} className="border-b border-slate-100 last:border-0 hover:bg-red-50/40" data-testid={`diff-row-${k}`}>
                      <td className="py-2 px-2 text-[12px] text-slate-800 font-semibold">{prettifyLabel(k)}</td>
                      <td className="py-2 px-2 text-[12px] text-slate-400 line-through" data-testid={`diff-before-${k}`}>
                        {formatDiffValue(k, before, unitMap, deptMap)}
                      </td>
                      <td className="py-2 px-2 text-[12px] text-red-700 font-bold bg-red-50 rounded" data-testid={`diff-after-${k}`}>
                        {formatDiffValue(k, v, unitMap, deptMap)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Exit details for EMPLOYEE_EXIT ─────────────────── */}
      {req.change_type === "EMPLOYEE_EXIT" && req.exit_ref && (
        <div
          className="bg-white rounded-lg border-l-4 border-l-red-500 border border-slate-200 p-5"
          data-testid="exit-detail-card"
        >
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">
            Exit Request — {req.exit_ref.exit_type}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <InfoRow icon={Calendar} label="Resignation date">
              {req.exit_ref.resignation_date || "—"}
            </InfoRow>
            <InfoRow icon={Calendar} label="Requested LWD">
              {req.exit_ref.requested_lwd || "—"}
            </InfoRow>
            <InfoRow icon={Clock} label="Notice shortfall">
              <span className={req.exit_ref.notice_shortfall_days > 0 ? "text-red-600 font-semibold" : ""}>
                {req.exit_ref.notice_shortfall_days} day{req.exit_ref.notice_shortfall_days === 1 ? "" : "s"}
              </span>
            </InfoRow>
            <InfoRow icon={IndianRupee} label="Recovery estimate">
              ₹{fmt(req.exit_ref.notice_recovery_estimate)}
            </InfoRow>
          </div>
          {req.exit_ref.notes && (
            <div className="mt-3 text-sm bg-slate-50 border border-slate-200 rounded p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Notes
              </div>
              {req.exit_ref.notes}
            </div>
          )}
          <div className="mt-3 text-[11px] text-slate-500">
            On approval: employee status → <strong>Left</strong>, approved LWD is pinned;
            LWD side-effects (portal disable, payroll stop, biometric unmap) run automatically at LWD.
          </div>
        </div>
      )}

      {/* ── Salary snapshot ────────────────────────────────── */}
      {snap && !snap._masked && (
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">
              Salary Breakup
            </h3>
            <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[10px]">
              <ShieldAlert className="h-3 w-3 mr-1" />
              Estimate — statutory figures pending
            </Badge>
          </div>
          <div className="text-[10px] text-slate-500 mb-2">
            Frozen at submit · config: {snap.config_version}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SalarySection title="Earnings">
              <SalaryLine label="Basic" v={earn.basic} />
              <SalaryLine label="HRA" v={earn.hra} />
              <SalaryLine label="Conveyance" v={earn.conveyance} />
              <SalaryLine label="DA" v={earn.da} />
              <SalaryLine label="Statutory Bonus" v={earn.statutory_bonus} />
              <SalaryLine label="Total Monthly Earnings" v={earn.total_monthly_earnings} bold />
            </SalarySection>
            <SalarySection title="Employee Deductions">
              <SalaryLine label="PF Employee" v={ded.pf_employee} />
              <SalaryLine label="ESI Employee (on Gross)" v={ded.esi_employee} />
              <SalaryLine label="Professional Tax" v={ded.professional_tax} />
              <SalaryLine label="Total Deductions" v={ded.total_deductions} bold />
            </SalarySection>
            <SalarySection title="Employer Contributions">
              <SalaryLine label="Employer EPF" v={er.epf_employer} />
              <SalaryLine label="Employer EPS" v={er.eps_employer} />
              <SalaryLine label="EDLI" v={er.edli} />
              <SalaryLine label="PF Admin" v={er.pf_admin} />
              <SalaryLine label="ESI Employer (on Gross)" v={er.esi_employer} />
              <SalaryLine label="Gratuity" v={er.gratuity} />
            </SalarySection>
            <SalarySection title="Net & CTC">
              <SalaryLine label="Net Salary" v={net.net_salary_before_bond} bold />
              <SalaryLine label="In-hand Salary" v={net.in_hand_salary} bold />
              <SalaryLine label="Monthly CTC" v={ctcBlock.monthly_ctc} bold />
              <SalaryLine label="Annual CTC" v={ctcBlock.annual_ctc} bold />
            </SalarySection>
          </div>
        </div>
      )}
      {snap && snap._masked && (
        <div className="bg-slate-50 rounded-lg border border-slate-200 p-5 text-sm text-slate-500">
          Salary breakup is hidden — hr_admin or super_admin role required.
        </div>
      )}
      {!snap && (
        <div className="bg-white rounded-lg border border-dashed border-slate-300 p-5 text-sm text-slate-400 text-center">
          No salary snapshot on this request.
        </div>
      )}

      {/* ── Approve dialog ────────────────────────────────── */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve {req.employee_name_at_submit}?</DialogTitle>
            <DialogDescription>
              Employee will become active immediately. Payroll and portal
              access can be enabled from the employee record.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Optional note</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Any note for the maker…"
              data-testid="approve-reason-input"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApproveOpen(false)}>Cancel</Button>
            <Button
              onClick={submitApprove}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="approve-confirm-btn"
            >
              Confirm Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Reject dialog ─────────────────────────────────── */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject {req.employee_name_at_submit}?</DialogTitle>
            <DialogDescription>
              The quarantined employee row will be permanently removed.
              The maker will see your reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>
              Reason <span className="text-red-600">*</span>
              <span className="text-[10px] text-slate-500 ml-1">(min 3 chars, required)</span>
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Explain why this is being rejected…"
              data-testid="reject-reason-input"
              rows={3}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button
              onClick={submitReject}
              className="bg-red-600 hover:bg-red-700"
              disabled={reason.trim().length < 3}
              data-testid="reject-confirm-btn"
            >
              Confirm Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-3.5 w-3.5 text-slate-400 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-sm text-slate-900 truncate">{children}</div>
      </div>
    </div>
  );
}

function SalarySection({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
        {title}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function SalaryLine({ label, v, bold }) {
  return (
    <div className={`flex justify-between text-sm ${bold ? "font-bold text-slate-900 border-t border-slate-200 pt-1 mt-1" : "text-slate-700"}`}>
      <span>{label}</span>
      <span className="font-mono">{v != null ? `₹${fmt(v)}` : "—"}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────

export default function Approvals() {
  const { user } = useAuth();
  const [tab, setTab] = useState("PENDING");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Rev. 2026-08-04 — Lookup maps for pretty diff rendering
  // (unit_id / department_id → human-readable name).
  const [unitMap, setUnitMap] = useState({});
  const [deptMap, setDeptMap] = useState({});
  // Rev. 2026-08-23 — Bulk Approve Wizard (super_admin only).
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDlgOpen, setBulkDlgOpen] = useState(false);
  const [bulkReason, setBulkReason] = useState("");
  const [bulkRunning, setBulkRunning] = useState(false);
  const isSuperAdmin = user?.role === "super_admin";

  useEffect(() => {
    (async () => {
      try {
        const [u, d] = await Promise.all([
          api.get("/units"),
          api.get("/departments"),
        ]);
        const uArr = Array.isArray(u.data) ? u.data : (u.data?.rows || []);
        const dArr = Array.isArray(d.data) ? d.data : (d.data?.rows || []);
        setUnitMap(Object.fromEntries(uArr.map((r) => [r.id, r.name])));
        setDeptMap(Object.fromEntries(dArr.map((r) => [r.id, r.name])));
      } catch { /* silent — fallback to raw UUID display */ }
    })();
  }, []);

  const loadList = async () => {
    setLoading(true);
    try {
      let url;
      if (tab === "MINE") {
        url = "/approvals/mine";
      } else if (tab === "HISTORY") {
        url = "/approvals?status=ALL";
      } else {
        url = "/approvals?status=PENDING";
      }
      const { data } = await api.get(url);
      let list = data.rows || [];
      if (tab === "HISTORY") {
        list = list.filter((r) => r.status !== "PENDING");
      }
      setRows(list);
      // Auto-select first row when tab changes
      if (list.length && !list.find((r) => r.id === activeId)) {
        setActiveId(list[0].id);
      }
      if (!list.length) setActiveId(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadList(); setBulkMode(false); setSelectedIds(new Set()); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeId) { setDetail(null); return; }
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      try {
        const { data } = await api.get(`/approvals/${activeId}`);
        if (!cancelled) setDetail(data);
      } catch (e) {
        if (!cancelled) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail));
          setDetail(null);
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeId]);

  const doApprove = async (id, reason) => {
    await api.post(`/approvals/${id}/approve`, { reason: reason || null });
    await loadList();
    // Refresh detail
    const { data } = await api.get(`/approvals/${id}`);
    setDetail(data);
  };
  const doReject = async (id, reason) => {
    await api.post(`/approvals/${id}/reject`, { reason });
    await loadList();
    const { data } = await api.get(`/approvals/${id}`);
    setDetail(data);
  };

  const pendingCount = useMemo(
    () => rows.filter((r) => r.status === "PENDING").length,
    [rows],
  );

  const selectableIds = useMemo(
    () => rows.filter((r) => r.status === "PENDING" && r.maker_id !== user?.id).map((r) => r.id),
    [rows, user?.id],
  );

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.size === selectableIds.length ? new Set() : new Set(selectableIds));
  };

  const submitBulkApprove = async () => {
    setBulkRunning(true);
    try {
      const { data } = await api.post("/approvals/bulk-approve", {
        ids: Array.from(selectedIds),
        reason: bulkReason || null,
      });
      const parts = [];
      if (data.approved) parts.push(`${data.approved} approved`);
      if (data.skipped) parts.push(`${data.skipped} skipped`);
      if (data.failed) parts.push(`${data.failed} failed`);
      if (data.failed > 0) {
        toast.error(`Bulk approve: ${parts.join(", ")}`);
      } else {
        toast.success(`Bulk approve: ${parts.join(", ") || "nothing to do"}`);
      }
      setBulkDlgOpen(false);
      setBulkReason("");
      setBulkMode(false);
      setSelectedIds(new Set());
      await loadList();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk approve failed");
    } finally {
      setBulkRunning(false);
    }
  };

  return (
    <div data-testid="approvals-page" className="space-y-4 h-[calc(100vh-100px)] flex flex-col">
      <div className="flex items-center gap-3 flex-wrap">
        <CheckSquare className="h-7 w-7 text-red-600" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Phase B</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Approvals Inbox
          </h1>
        </div>
        {isSuperAdmin && tab === "PENDING" && pendingCount > 0 && (
          <Button
            variant={bulkMode ? "default" : "outline"}
            size="sm"
            className={bulkMode ? "bg-red-600 hover:bg-red-700" : ""}
            onClick={() => { setBulkMode((v) => !v); setSelectedIds(new Set()); }}
            data-testid="toggle-bulk-mode-btn"
          >
            <CheckSquare className="h-3.5 w-3.5 mr-1.5" />
            {bulkMode ? "Cancel Bulk Approve" : "Bulk Approve"}
          </Button>
        )}
      </div>

      {bulkMode && (
        <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-red-600"
              checked={selectedIds.size > 0 && selectedIds.size === selectableIds.length}
              onChange={toggleSelectAll}
              data-testid="bulk-select-all"
            />
            <span className="text-slate-700">
              Select all eligible ({selectableIds.length})
            </span>
          </label>
          <Button
            size="sm"
            disabled={selectedIds.size === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => setBulkDlgOpen(true)}
            data-testid="open-bulk-approve-dialog-btn"
          >
            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
            Approve Selected ({selectedIds.size})
          </Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col min-h-0">
        <TabsList data-testid="approvals-tabs">
          <TabsTrigger value="PENDING" data-testid="tab-pending">
            Pending
            {tab === "PENDING" && pendingCount > 0 && (
              <Badge className="ml-2 bg-red-500 text-white">{pendingCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="MINE" data-testid="tab-mine">My Requests</TabsTrigger>
          <TabsTrigger value="HISTORY" data-testid="tab-history">History</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="flex-1 min-h-0">
          <div className="grid grid-cols-1 md:grid-cols-[minmax(340px,1fr)_2fr] gap-0 border border-slate-200 rounded-lg overflow-hidden h-full">
            <RequestListPane
              rows={rows}
              activeId={activeId}
              onSelect={setActiveId}
              loading={loading}
              bulkMode={bulkMode}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              currentUser={user}
              emptyMsg={
                tab === "PENDING" ? "No pending approvals — you're all caught up." :
                tab === "MINE"    ? "You haven't submitted any requests yet." :
                                    "No historical requests."
              }
            />
            <DetailPane
              detail={detail}
              currentUser={user}
              onApprove={doApprove}
              onReject={doReject}
              loading={detailLoading}
              unitMap={unitMap}
              deptMap={deptMap}
            />
          </div>
        </TabsContent>
      </Tabs>

      {/* ── Bulk approve confirmation ─────────────────────── */}
      <Dialog open={bulkDlgOpen} onOpenChange={setBulkDlgOpen}>
        <DialogContent data-testid="bulk-approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve {selectedIds.size} requests?</DialogTitle>
            <DialogDescription>
              Each request is applied independently — if one fails (e.g. you
              submitted it yourself), the rest still go through and you'll
              see a summary of what happened.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Optional note (applied to all)</Label>
            <Textarea
              value={bulkReason}
              onChange={(e) => setBulkReason(e.target.value)}
              placeholder="Any note for the makers…"
              data-testid="bulk-approve-reason-input"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBulkDlgOpen(false)}>Cancel</Button>
            <Button
              onClick={submitBulkApprove}
              disabled={bulkRunning}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="bulk-approve-confirm-btn"
            >
              {bulkRunning ? "Approving…" : `Confirm Approve ${selectedIds.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
